import React, { useState, useEffect } from 'react';
import { aiChatApi, AIChatConversation, AIChatStatistics } from '../services/supportApi';
import './ChatbotConversations.css';

const ChatbotConversations: React.FC = () => {
  const [conversations, setConversations] = useState<AIChatConversation[]>([]);
  const [statistics, setStatistics] = useState<AIChatStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedConversation, setSelectedConversation] = useState<AIChatConversation | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 20;

  // Filters
  const [filterUserId, setFilterUserId] = useState<number | undefined>(undefined);
  const [filterConversationId, setFilterConversationId] = useState<string>('');
  const [filterLanguage, setFilterLanguage] = useState<'en' | 'ar' | undefined>(undefined);
  const [filterConverted, setFilterConverted] = useState<boolean | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    loadConversations();
    loadStatistics();
  }, [currentPage, filterUserId, filterConversationId, filterLanguage, filterConverted]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const params: any = {
        page: currentPage,
        size: pageSize,
      };

      if (filterUserId) {
        params.userId = filterUserId;
      }
      if (filterConversationId) {
        params.conversationId = filterConversationId;
      }
      if (filterLanguage) {
        params.language = filterLanguage;
      }
      if (filterConverted !== undefined) {
        params.converted = filterConverted;
      }

      const data = await aiChatApi.getConversations(token, params);
      setConversations(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
    } catch (error: any) {
      console.error('Failed to load conversations:', error);
      setError(error.message || 'Failed to load chatbot conversations');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      setLoadingStats(true);
      const token = localStorage.getItem('adminToken');
      if (!token) {
        return;
      }

      const stats = await aiChatApi.getStatistics(token);
      setStatistics(stats);
    } catch (error: any) {
      console.error('Failed to load statistics:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleSelectConversation = (conversation: AIChatConversation) => {
    setSelectedConversation(conversation);
  };

  const handleClearFilters = () => {
    setFilterUserId(undefined);
    setFilterConversationId('');
    setFilterLanguage(undefined);
    setFilterConverted(undefined);
    setSearchQuery('');
    setCurrentPage(0);
  };

  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      conv.userMessage.toLowerCase().includes(query) ||
      conv.aiResponse.toLowerCase().includes(query) ||
      conv.user?.name?.toLowerCase().includes(query) ||
      conv.user?.phoneNumber?.includes(query) ||
      conv.intent?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getIntentColor = (intent?: string) => {
    if (!intent) return '#999';
    const colors: { [key: string]: string } = {
      find_technician: '#2196F3',
      estimate_cost: '#FF9800',
      explore_services: '#4CAF50',
      general_query: '#9C27B0',
    };
    return colors[intent] || '#999';
  };

  return (
    <div className="chatbot-conversations">
      <div className="cc-header">
        <h2>AI Chatbot Conversations</h2>
        <button className="btn-refresh" onClick={() => { loadConversations(); loadStatistics(); }} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="cc-statistics">
          <div className="cc-stat-card">
            <div className="cc-stat-value">{statistics.totalConversations.toLocaleString()}</div>
            <div className="cc-stat-label">Total Conversations</div>
          </div>
          <div className="cc-stat-card">
            <div className="cc-stat-value">{statistics.totalUsers.toLocaleString()}</div>
            <div className="cc-stat-label">Total Users</div>
          </div>
          <div className="cc-stat-card">
            <div className="cc-stat-value">{statistics.conversionRate.toFixed(1)}%</div>
            <div className="cc-stat-label">Conversion Rate</div>
          </div>
          <div className="cc-stat-card">
            <div className="cc-stat-value">{statistics.averageConversationsPerUser.toFixed(1)}</div>
            <div className="cc-stat-label">Avg per User</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="cc-filters">
        <div className="cc-filter-group">
          <label>User ID:</label>
          <input
            type="number"
            value={filterUserId || ''}
            onChange={(e) => setFilterUserId(e.target.value ? parseInt(e.target.value) : undefined)}
            placeholder="Filter by user ID"
          />
        </div>
        <div className="cc-filter-group">
          <label>Conversation ID:</label>
          <input
            type="text"
            value={filterConversationId}
            onChange={(e) => setFilterConversationId(e.target.value)}
            placeholder="Filter by conversation ID"
          />
        </div>
        <div className="cc-filter-group">
          <label>Language:</label>
          <select
            value={filterLanguage || ''}
            onChange={(e) => setFilterLanguage(e.target.value ? e.target.value as 'en' | 'ar' : undefined)}
          >
            <option value="">All Languages</option>
            <option value="en">English</option>
            <option value="ar">Arabic</option>
          </select>
        </div>
        <div className="cc-filter-group">
          <label>Converted:</label>
          <select
            value={filterConverted === undefined ? 'all' : filterConverted ? 'yes' : 'no'}
            onChange={(e) => {
              if (e.target.value === 'all') setFilterConverted(undefined);
              else setFilterConverted(e.target.value === 'yes');
            }}
          >
            <option value="all">All</option>
            <option value="yes">Converted to Live Agent</option>
            <option value="no">Not Converted</option>
          </select>
        </div>
        <button className="btn-clear-filters" onClick={handleClearFilters}>
          Clear Filters
        </button>
      </div>

      {/* Search */}
      <div className="cc-search">
        <input
          type="text"
          placeholder="Search conversations by message, user, or intent..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="cc-content">
        {/* Conversations List */}
        <div className="cc-conversations-panel">
          <div className="cc-conversations-header">
            <h3>Conversations ({totalElements})</h3>
          </div>
          <div className="cc-conversations-list">
            {loading ? (
              <div className="cc-loading">
                <p>Loading conversations...</p>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="cc-empty">
                <p>No conversations found</p>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`cc-conversation-item ${
                    selectedConversation?.id === conv.id ? 'active' : ''
                  } ${conv.convertedToLiveAgent ? 'converted' : ''}`}
                  onClick={() => handleSelectConversation(conv)}
                >
                  <div className="cc-conv-header">
                    <div className="cc-conv-user">
                      {conv.user?.name || 'Anonymous User'}
                      {conv.user?.phoneNumber && <span className="cc-conv-phone">{conv.user.phoneNumber}</span>}
                    </div>
                    <div className="cc-conv-time">{formatDate(conv.createdAt)}</div>
                  </div>
                  <div className="cc-conv-preview">
                    <div className="cc-conv-user-message">
                      <strong>User:</strong> {conv.userMessage.substring(0, 100)}
                      {conv.userMessage.length > 100 && '...'}
                    </div>
                    <div className="cc-conv-ai-response">
                      <strong>AI:</strong> {conv.aiResponse.substring(0, 100)}
                      {conv.aiResponse.length > 100 && '...'}
                    </div>
                  </div>
                  <div className="cc-conv-meta">
                    {conv.intent && (
                      <span
                        className="cc-conv-intent"
                        style={{ backgroundColor: getIntentColor(conv.intent) }}
                      >
                        {conv.intent}
                      </span>
                    )}
                    {conv.language && (
                      <span className="cc-conv-language">{conv.language.toUpperCase()}</span>
                    )}
                    {conv.convertedToLiveAgent && (
                      <span className="cc-conv-converted">✓ Converted</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="cc-pagination">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
              >
                Previous
              </button>
              <span>
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
                disabled={currentPage >= totalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Conversation Details */}
        <div className="cc-details-panel">
          {selectedConversation ? (
            <>
              <div className="cc-details-header">
                <h3>Conversation Details</h3>
                <button className="btn-close" onClick={() => setSelectedConversation(null)}>
                  ×
                </button>
              </div>
              <div className="cc-details-content">
                <div className="cc-detail-section">
                  <h4>User Information</h4>
                  {selectedConversation.user ? (
                    <>
                      <p>
                        <strong>Name:</strong> {selectedConversation.user.name}
                      </p>
                      <p>
                        <strong>Phone:</strong> {selectedConversation.user.phoneNumber}
                      </p>
                      {selectedConversation.user.email && (
                        <p>
                          <strong>Email:</strong> {selectedConversation.user.email}
                        </p>
                      )}
                      <p>
                        <strong>User ID:</strong> {selectedConversation.user.id}
                      </p>
                    </>
                  ) : (
                    <p>Anonymous user</p>
                  )}
                  <p>
                    <strong>Conversation ID:</strong> {selectedConversation.conversationId}
                  </p>
                </div>

                <div className="cc-detail-section">
                  <h4>User Message</h4>
                  <div className="cc-message-box user-message">
                    {selectedConversation.userMessage}
                  </div>
                </div>

                <div className="cc-detail-section">
                  <h4>AI Response</h4>
                  <div className="cc-message-box ai-message">
                    {selectedConversation.aiResponse}
                  </div>
                </div>

                <div className="cc-detail-section">
                  <h4>Metadata</h4>
                  <div className="cc-metadata">
                    <div className="cc-metadata-item">
                      <strong>Intent:</strong>{' '}
                      {selectedConversation.intent || 'N/A'}
                    </div>
                    <div className="cc-metadata-item">
                      <strong>Language:</strong>{' '}
                      {selectedConversation.language?.toUpperCase() || 'N/A'}
                    </div>
                    <div className="cc-metadata-item">
                      <strong>Converted to Live Agent:</strong>{' '}
                      {selectedConversation.convertedToLiveAgent ? 'Yes' : 'No'}
                    </div>
                    {selectedConversation.supportRequestId && (
                      <div className="cc-metadata-item">
                        <strong>Support Request ID:</strong>{' '}
                        {selectedConversation.supportRequestId}
                      </div>
                    )}
                    {selectedConversation.detectedServices &&
                      selectedConversation.detectedServices.length > 0 && (
                        <div className="cc-metadata-item">
                          <strong>Detected Services:</strong>{' '}
                          {selectedConversation.detectedServices.map(s => s.nameEn).join(', ')}
                        </div>
                      )}
                    {selectedConversation.detectedRegions &&
                      selectedConversation.detectedRegions.length > 0 && (
                        <div className="cc-metadata-item">
                          <strong>Detected Regions:</strong>{' '}
                          {selectedConversation.detectedRegions.map(r => r.nameEn).join(', ')}
                        </div>
                      )}
                    <div className="cc-metadata-item">
                      <strong>Created At:</strong> {formatDate(selectedConversation.createdAt)}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="cc-empty-details">
              <p>Select a conversation to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatbotConversations;

